import React, { useState, useEffect, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import useDebouncedSearchWithHistory from '../../../../../hooks/useDebouncedSearchWithHistory';
import { UiContext } from '../../../../../context/UiContext';
import tableColsToSortOptions from '../../../../../helpers/tableColsToSortOptions';
import AdminService from '../../../../../services/AdminService';
import TableMenu from '../../TableMenu';
import Table from '../../../../Table';
import ViewCompany from './view/ViewCompany';
import AddOrEditCompany from './edit/AddOrEditCompany';

export default function Companies({ setItemCount }) {
  const [activeAction, setActiveAction] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedRows, setSelectedRows] = useState([]);
  const [isViewVisible, setIsViewVisible] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [isAddVisible, setIsAddVisible] = useState(false);
  const [isEditVisible, setIsEditVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { t } = useTranslation();
  const [activeSortOption, setActiveSortOption] = useState({
    label: t('common.createdOn'),
    value: 'createdAt',
  });

  const {
    showModal,
    showUnknownErrorModal,
    showSelectionNeededModal,
    setIsFetching,
    showConflictModal,
  } = useContext(UiContext);

  const { debouncedSearchTerm, searchHistory } = useDebouncedSearchWithHistory(
    searchTerm,
    'companiesSearchHistory'
  );

  const {
    data: companiesPages,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [
      'companies',
      sortOrder,
      activeSortOption.value,
      debouncedSearchTerm,
      currentPage,
    ],
    queryFn: () =>
      AdminService.getCompanies({
        orderBy: activeSortOption.value || 'id',
        term: debouncedSearchTerm,
        sortOrder,
        pageNumber: currentPage,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (companiesPages) {
      setItemCount(companiesPages.totalCount);
    }
  }, [companiesPages, setItemCount]);

  const rows = companiesPages?.data || [];

  useEffect(() => {
    refetch();
  }, [activeSortOption, debouncedSearchTerm, refetch, sortOrder]);

  const showViewData = (id) => {
    setCurrentId(id);
    setIsViewVisible(true);
  };

  const showEdit = (id) => {
    if (id) {
      setSelectedRows([id]);
    }
    setIsEditVisible(true);
  };

  const deleteCompanies = async () => {
    try {
      setIsFetching(true);
      const promises = selectedRows.map((row) =>
        AdminService.deleteCompany({ id: row })
      );
      await Promise.all(promises);
      showModal({
        title: t(
          'dashboardComponents.Console.CompanyManagement.view.successTitle'
        ),
        text: t(
          'dashboardComponents.Console.CompanyManagement.view.successText'
        ),
        dismissButtonLabel: t('common.gotIt'),
        dismissButtonVariant: '',
      });
    } catch (error) {
      if (error.response.status === 409) {
        showConflictModal(error.response.data.message);
      } else {
        showUnknownErrorModal();
      }
    } finally {
      await refetch();
      setIsFetching(false);
    }
  };

  const columns = [
    {
      name: t('dashboardComponents.Console.CompanyManagement.columns.Logo'),
      field: 'logo',
      sortable: false,
      type: 'companyLogo',
    },
    {
      name: t('dashboardComponents.Console.CompanyManagement.columns.Name'),
      field: 'name',
      sortable: true,
    },
    {
      name: t('dashboardComponents.Console.CompanyManagement.columns.Type'),
      field: 'type',
      sortable: true,
    },
    {
      name: t('dashboardComponents.Console.CompanyManagement.columns.Address'),
      field: 'address',
      sortable: true,
    },
    {
      name: t('dashboardComponents.Console.CompanyManagement.columns.Country'),
      field: 'country',
      sortable: true,
    },
    {
      name: t('dashboardComponents.Console.CompanyManagement.columns.Contact'),
      field: 'primaryManager',
      type: 'primaryManager',
      sortField: 'primaryManager.lastName',
      sortable: true,
    },
    {
      name: t('common.createdOn'),
      field: 'createdAt',
      sortable: true,
      type: 'date',
    },
    {
      name: t('dashboardComponents.Console.CompanyManagement.columns.Status'),
      field: 'status',
      sortable: true,
      type: 'companyStatus',
    },
  ];

  const actions = [
    {
      label: t('common.add'),
      value: 'Add',
      onClick: () => setIsAddVisible(true),
    },
    {
      label: t('common.edit'),
      value: 'Edit',
      onClick: () => {
        if (selectedRows.length) {
          showEdit();
        } else {
          showSelectionNeededModal();
        }
      },
    },
    {
      label: t('common.delete'),
      value: 'Delete',
      onClick: () => {
        if (selectedRows.length) {
          showModal({
            icon: 'delete',
            title: t(
              'dashboardComponents.Console.CompanyManagement.view.deleteConfirmationTitle'
            ),
            text: t(
              'dashboardComponents.Console.CompanyManagement.view.deleteConfirmationText'
            ),
            dismissButtonLabel: t('common.no'),
            confirmButtonLabel: t('common.yes'),
            onConfirm: deleteCompanies,
          });
        } else {
          showSelectionNeededModal();
        }
      },
    },
  ];

  const sortOptions = tableColsToSortOptions(columns);

  return (
    <div>
      <TableMenu
        actions={actions}
        activeAction={activeAction}
        setActiveAction={setActiveAction}
        dangerAction="Delete"
        sortOptions={sortOptions}
        activeSortOption={activeSortOption}
        setActiveSortOption={setActiveSortOption}
        selectedCount={selectedRows.length}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchHistory={searchHistory}
        setSortOrder={setSortOrder}
        currentPage={currentPage}
        totalPages={companiesPages?.totalPages || 1}
        setCurrentPage={setCurrentPage}
        searchResultCount={
          debouncedSearchTerm && !isFetching ? companiesPages?.totalCount : null
        }
      />
      <Table
        name="Companies"
        rows={rows}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sortOptions={sortOptions}
        setActiveSortOption={setActiveSortOption}
        activeSortOption={activeSortOption}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        columns={columns}
        onRowClick={showViewData}
      />
      <ViewCompany
        currentId={currentId}
        isVisible={isViewVisible}
        handleClose={() => setIsViewVisible(false)}
        companies={rows}
        refetch={refetch}
        isOverlayed={isEditVisible}
        showEdit={showEdit}
      />

      <AddOrEditCompany
        isVisible={isAddVisible}
        handleClose={() => setIsAddVisible(false)}
        refetch={refetch}
        showAgain={() => setIsAddVisible(true)}
        isAdd
        companies={rows}
      />

      <AddOrEditCompany
        isVisible={isEditVisible}
        handleClose={() => setIsEditVisible(false)}
        refetch={refetch}
        isEdit
        selectedRows={selectedRows}
        companies={rows}
        hasNoAnimation={isViewVisible}
      />
    </div>
  );
}
